import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { task, type TaskForAsyncTaskFunction } from 'ember-concurrency';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { AppliedMappingFilterIds } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import type HotelSettingsCentralMappingHierarchyController from 'lumen/controllers/hotel/settings/central-mapping/hierarchy';
import type HotelSettingsCentralMappingMappingController from 'lumen/controllers/hotel/settings/central-mapping/mapping';
import type MarketSettingsCentralMappingHierarchyController from 'lumen/controllers/market/settings/central-mapping/hierarchy';
import type MarketSettingsCentralMappingMappingController from 'lumen/controllers/market/settings/central-mapping/mapping';
import type {
  CentralMappingBreakdown,
  CentralMappingCategory,
  CentralMappingMarketSubscription,
} from 'lumen/models';
import type {
  MappingEntriesRawResponseMeta,
  MappingEntriesResponseMeta,
} from 'lumen/serializers/central-mapping/mapping-entry';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import {
  type Breakdown,
  BREAKDOWNS,
  BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT,
  CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS,
  CENTRAL_MAPPING_UPLOAD_ERROR_CODES,
  CENTRAL_MAPPING_UPLOAD_ERROR_CODES_LOGGED_TO_SENTRY,
  CENTRAL_MAPPING_UPLOAD_ROW_LEVEL_ERROR_TRANSLATIONS,
  type CentralMappingFilterQueryParams,
  type CentralMappingMappingEntryExtendedModel,
  type CentralMappingUploadRowLevelErrorCode,
  HIDDEN_PARENT_CATEGORY_ID,
  LARGE_MARKET_PROPERTY_LIMIT,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import getCentralMappingUploadErrorInfo, {
  type CentralMappingUploadErrorInfo,
  formatRowList,
} from 'lumen/utils/central-mapping-upload-error-message';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type CentralMappingExtendedController =
  | HotelSettingsCentralMappingHierarchyController
  | HotelSettingsCentralMappingMappingController
  | MarketSettingsCentralMappingHierarchyController
  | MarketSettingsCentralMappingMappingController;

const controllerIsForMappingRoute = (
  controller: CentralMappingExtendedController,
): controller is HotelSettingsCentralMappingMappingController => {
  return 'handleUnsavedChanges' in controller;
};

export default class CentralMappingService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  getBreakdownsTask = task({ restartable: true }, async () => {
    try {
      const breakdowns = await this.store.query<CentralMappingBreakdown>(
        'central-mapping/breakdown',
        {
          namespace: APIGATEWAY,
          typeKey: 'mapping',
          [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
        },
      );

      return breakdowns;
    } catch (error) {
      void this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  getHierarchyTask = task(
    { restartable: true },
    async (breakdown: Breakdown, managedBy?: string) => {
      let hierarchyResponse = null;
      const params: Record<string, string | string[] | number | null> = {
        namespace: APIGATEWAY,
        typeKey: 'mapping/categories',
        [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
        breakdown,
      };

      if (managedBy) {
        params['managed_by'] = managedBy;
      }

      try {
        hierarchyResponse = await this.store.query<CentralMappingCategory>(
          'central-mapping/category',
          params,
        );

        let hierarchy = [...hierarchyResponse];

        const isNonFixedRootHierarchy = hierarchy.every((category) => {
          return !category.isFixed;
        });

        if (isNonFixedRootHierarchy) {
          // If the hierarchy does not have fixed root categories,
          // we need to create a hidden parent category to act as a parent to group all the non-fixed categories.
          // The hidden parent category will not be shown in the UI.
          let rootCategory = this.store.peekRecord<CentralMappingCategory>(
            'central-mapping/category',
            `${HIDDEN_PARENT_CATEGORY_ID}-${breakdown}`,
          );

          if (!rootCategory) {
            rootCategory = this.store.createRecord<CentralMappingCategory>(
              'central-mapping/category',
              {
                id: `${HIDDEN_PARENT_CATEGORY_ID}-${breakdown}`,
                name: '',
                isFixed: false,
                parent: null,
                children: [],
                // @ts-expect-error: Incorrect type
                valueType: breakdown,
                isHiddenParent: true,
              },
            );
          }

          hierarchy.forEach((category) => {
            if (!category.parent) {
              category.parent = rootCategory;

              // This is needed to get rid of the hasDirtyRelationships triggered by the parent assignment.
              // The parent assignment will not be persisted to the BE, so we need to update the relationships here.
              category.updateRelationships();
            }
            return category;
          });

          hierarchy = [...hierarchy, rootCategory];
        }

        return {
          hierarchy,
        };
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getMappingEntriesTask = task(
    { restartable: true },
    async ({
      breakdown,
      valueType,
      pageSize,
      page,
      filterOptions = {},
      managedBy,
      includeArchived,
    }: {
      breakdown: Breakdown;
      filterOptions?: CentralMappingFilterQueryParams;
      includeArchived?: boolean;
      managedBy?: string;
      page?: number;
      pageSize?: number;
      valueType: PmsValueType;
    }) => {
      try {
        // First, fetch the hierarchy, so Ember Data can link hierarchy categories in the store to the mapping entries automatically.
        await this.getHierarchyTask.perform(breakdown, managedBy);

        // If this is a market, fetch the market subscription to link the market info to the mapping entries.
        if (this.hotelAndMarketService.market) {
          const marketSubscriptions =
            await this.getMarketSubscriptionTask.perform();
          /*
           * If there are more than LARGE_MARKET_PROPERTY_LIMIT properties in the market, and there are no property filters active,
           * and the breakdown requires initial filters to be applied (i.e. all except DAY_OF_WEEK and GUEST_COUNTRY),
           * then do not fetch the mapping entries and return null instead.
           * This is to prevent performance issues when loading the mapping page for large markets.
           */
          if (
            !BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT.includes(breakdown) &&
            !this.hasActivePropertyFilters(filterOptions) &&
            marketSubscriptions.length > LARGE_MARKET_PROPERTY_LIMIT
          ) {
            return null;
          }
        }

        const mappingEntryModelName =
          this.getMappingEntryModelNameByBreakdown(breakdown);

        // Clear the mapping entries from the store before fetching them.
        // Users might arrive at the mapping page while automapping is in progress. In that case, the first fetch will
        // have the old state of the mapping entries. After automapping is complete, we fetch the same mapping entries again,
        // but the new state might show as unsaved changes (dirty attributes) instead.
        this.store.unloadAll(mappingEntryModelName);

        const params: Record<string, unknown> = {
          namespace: APIGATEWAY,
          typeKey: 'mapping/mapping_entries',
          [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id,
          breakdown,
          value_type: valueType,
        };
        if (page) {
          params['page'] = page;
        }
        if (pageSize) {
          params['page_size'] = pageSize;
        }
        if (managedBy) {
          params['managed_by'] = managedBy;
        }
        if (includeArchived) {
          params['include_archived'] = includeArchived;
        }

        const {
          chains,
          brands,
          properties,
          view,
          orderBy,
          search,
          barBased,
          commissionable,
          categories,
        } = filterOptions;
        if (orderBy) {
          params['order_by'] = orderBy;
        }
        if (view) {
          params['view'] = view;
        }
        if (search) {
          params['search'] = search;
        }
        if (chains?.length) {
          params['chains'] = chains.split(',');
        }
        if (brands?.length) {
          params['brands'] = brands.split(',');
        }
        if (properties?.length) {
          params['subscriptions'] = properties.split(',');
        }
        if (barBased) {
          params['bar_based_view'] = barBased;
        }
        // Commissionable only applies to market-segment rate plans, so never leak a
        // stale filter value onto other breakdowns/value types.
        if (
          commissionable &&
          breakdown === BREAKDOWNS.MARKET_SEGMENT &&
          valueType === PMS_VALUE_TYPES.RATE_CODE
        ) {
          params['commissionable_view'] = commissionable;
        }
        if (categories?.length) {
          params['categories'] = categories.split(',');
        }

        const mappingEntriesResponse =
          await this.store.query<CentralMappingMappingEntryExtendedModel>(
            // @ts-expect-error: Incorrect type
            mappingEntryModelName,
            params,
          );

        const meta =
          mappingEntriesResponse.meta as unknown as MappingEntriesResponseMeta;

        return {
          mappingEntries: Array.from(mappingEntriesResponse),
          savingStatus: meta.status,
          paginationMeta: meta.paginationMeta,
        };
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        return null;
      }
    },
  );

  getMarketSubscriptionTask = task({ restartable: true }, async () => {
    try {
      const marketId = this.hotelAndMarketService.hotelOrMarket.id;

      if (!marketId) {
        throw new Error(
          'Market ID is not available in getMarketSubscriptionTask',
        );
      }

      let marketSubscriptions = this.store
        .peekAll<CentralMappingMarketSubscription>(
          'central-mapping/market-subscription',
        )
        .filter((subscription) => {
          return subscription.marketId === marketId;
        });

      if (!marketSubscriptions.length) {
        // Clear any previously cached market subscriptions to ensure we fetch the latest data.
        this.store.unloadAll('central-mapping/market-subscription');

        marketSubscriptions =
          await this.store.query<CentralMappingMarketSubscription>(
            'central-mapping/market-subscription',
            {
              namespace: APIGATEWAY,
              typeKey: 'mapping/market_info',
              market_id: marketId,
            },
          );

        // Assign the market relationship to each fetched marketSubscription to enable filtering cached marketSubscriptions by market ID.
        marketSubscriptions.forEach((marketSubscription) => {
          marketSubscription.marketId = marketId;
        });
      }

      return marketSubscriptions;
    } catch (error) {
      void this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  showRetryAlertTask = task(
    { restartable: true },
    async (
      taskCallback: TaskForAsyncTaskFunction<
        {
          restartable: boolean;
        },
        () => Promise<{
          isBeingProcessed: boolean;
        } | null | void>
      >,
    ): Promise<void> => {
      const confirmWasClicked = await this.alerting.showErrorAlert({
        title: this.intl.t('settings.central_mapping.hierarchy.alert.title'),
        text: this.intl.t('settings.central_mapping.hierarchy.alert.text'),
        buttons: {
          cancel: {
            text: this.intl.t(
              'settings.central_mapping.hierarchy.alert.cancel',
            ),
          },
          confirm: {
            text: this.intl.t(
              'settings.central_mapping.hierarchy.alert.confirm',
            ),
          },
        },
      });

      if (confirmWasClicked) {
        void taskCallback.perform();
      }
    },
  );

  updateMappingTask = task(
    { restartable: true },
    async (
      mappingEntries: CentralMappingMappingEntryExtendedModel[],
      breakdown: Breakdown,
      valueType: PmsValueType,
      managedBy?: string,
    ) => {
      const serializedMappingEntries = mappingEntries.map((mappingEntry) =>
        mappingEntry.serialize({ includeId: true }),
      );

      const payload = {
        meta: { breakdown },
        data: serializedMappingEntries,
      };

      const params: Record<string, string> = {
        [this.hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id!,
        breakdown,
        value_type: valueType,
      };
      if (managedBy !== undefined) {
        params['managed_by'] = managedBy;
      }

      const url = formatUrl(
        '/apigateway/v1/app/mapping/mapping_entries',
        params,
        this.ajax.host,
      );

      const response = await this.ajax.post<{
        data: Record<string, unknown>[];
        meta: MappingEntriesRawResponseMeta;
      }>(url, {
        data: payload,
      });

      return response;
    },
  );

  get breakdowns(): CentralMappingBreakdown[] {
    if (!this.getBreakdownsTask.last?.value) {
      return [];
    }

    const breakdowns = this.getBreakdownsTask.last.value.slice();

    if (
      !this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.NET_REVENUE_SETTINGS)
    ) {
      return breakdowns.filter(
        ({ breakdown }) => breakdown !== BREAKDOWNS.DISTRIBUTION_CHANNEL,
      );
    }

    return breakdowns;
  }

  /**
   * Determines if the user has access to the central mapping settings.
   * Access is granted if the user has the central mapping feature flag enabled
   * or has product access (Pricing Assistant, Rates, Parity, or Benchmark Insight mapping tab).
   */
  get hasCentralMappingHotelAccess(): boolean {
    const hasProductAccess =
      this.access.hasPricingAssistantTab ||
      this.access.hasRates ||
      this.access.hasParity ||
      this.access.hasBenchmarkInsightMappingTab;

    return this.access.hasCentralMapping || hasProductAccess;
  }

  get hotelOrMarketKey(): string {
    return this.hotelAndMarketService.hotel ? 'subscription_id' : 'market_id';
  }

  get sortedBreakdowns(): CentralMappingBreakdown[] {
    const breakdownValues = Object.values(BREAKDOWNS);

    return this.breakdowns
      .filter((breakdown) => {
        return breakdown.available;
      })
      .sort((a, b) => {
        return (
          breakdownValues.indexOf(a.breakdown) -
          breakdownValues.indexOf(b.breakdown)
        );
      });
  }

  get sortedBreakdownsBenchmark(): CentralMappingBreakdown[] {
    return this.sortedBreakdowns.filter((breakdown) => {
      return breakdown.breakdown === BREAKDOWNS.BENCHMARK_MARKET_SEGMENT;
    });
  }

  get sortedBreakdownsBi(): CentralMappingBreakdown[] {
    const hasStayRestrictionRateGroupFt = this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.STAY_RESTRICTION_RATE_GROUP,
    );

    return this.sortedBreakdowns.filter((breakdown) => {
      if (breakdown.breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP) {
        return hasStayRestrictionRateGroupFt;
      }

      return breakdown.breakdown !== BREAKDOWNS.BENCHMARK_MARKET_SEGMENT;
    });
  }

  // The current hotel or market's display name, used to interpolate {name}
  // into upload error copy (e.g. multiple_hotels_in_file, no_relevant_active_product).
  get uploadContextName(): string {
    const { hotel, market } = this.hotelAndMarketService;

    return hotel ? hotel.hotelinfo.name : (market?.displayName ?? '');
  }

  getMappingEntryModelNameByBreakdown(breakdown: Breakdown): string {
    switch (breakdown) {
      case BREAKDOWNS.MARKET_SEGMENT:
      case BREAKDOWNS.RATE_CODE:
        return 'central-mapping/mapping-entry-market-segment';
      case BREAKDOWNS.ROOM_TYPE:
        return 'central-mapping/mapping-entry-room-type';
      case BREAKDOWNS.DAY_OF_WEEK:
        return 'central-mapping/mapping-entry-day-of-week';
      default:
        // These breakdowns (DISTRIBUTION_CHANNEL, TRAVEL_AGENT, GUEST_COUNTRY, COMPANY, SOURCE, ORIGIN) are handled by the default mapping entry model.
        return 'central-mapping/mapping-entry';
    }
  }

  // Picks the right message variant for a row-level error: no rows, one row,
  // a few rows listed by number, or a truncated "N rows, including ..." summary.
  private getRowLevelUploadErrorMessage(
    info: CentralMappingUploadErrorInfo,
    code: CentralMappingUploadRowLevelErrorCode,
  ): string {
    const translations =
      CENTRAL_MAPPING_UPLOAD_ROW_LEVEL_ERROR_TRANSLATIONS[code];
    const rows = info.rows ?? [];

    if (rows.length === 0) {
      return this.intl.t(translations.generic);
    }

    if (rows.length === 1) {
      // hotel_not_found rows can be missing the hotel value entirely
      // (rather than naming an unrecognized one), which needs its own copy.
      if (translations.rowMissing && info.value === undefined) {
        return this.intl.t(translations.rowMissing, { row: rows[0] });
      }

      return this.intl.t(translations.row, {
        row: rows[0],
        column: info.column,
        value: info.value,
      });
    }

    const isTruncated = rows.length > 5;
    const rowList = formatRowList(
      isTruncated ? rows.slice(0, 5) : rows,
      this.intl.primaryLocale ?? 'en',
    );

    return this.intl.t(
      isTruncated ? translations.rowsTruncated : translations.rows,
      {
        column: info.column,
        count: rows.length,
        rowList,
      },
    );
  }

  getUploadErrorMessage(error: unknown): string {
    const info = getCentralMappingUploadErrorInfo(error, this.errorTracking);

    if (
      info.code &&
      CENTRAL_MAPPING_UPLOAD_ERROR_CODES_LOGGED_TO_SENTRY.includes(info.code)
    ) {
      this.errorTracking.captureException(error);
    }

    switch (info.code) {
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.HOTEL_MISMATCH:
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.HOTEL_NOT_FOUND:
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.HIERARCHY_PATH_NOT_FOUND:
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.INVALID_ROW_VALUE:
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.UNKNOWN_PMS_VALUE:
        return this.getRowLevelUploadErrorMessage(info, info.code);
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.MISSING_FILE:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.missing_file',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.FILE_TOO_LARGE:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.file_too_large',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.INVALID_EXCEL_FILE:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.invalid_excel_file',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.EMPTY_WORKBOOK:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.empty_workbook',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.HEADER_NOT_RECOGNIZED:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.header_not_recognized',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.MULTIPLE_HOTELS_IN_FILE:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.multiple_hotels_in_file',
          { name: this.uploadContextName },
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.MISSING_HOTEL_COLUMN:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.missing_hotel_column',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.INVALID_PARAMETER:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.invalid_parameter',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.SUBSCRIPTION_ACCESS_DENIED:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.subscription_access_denied',
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.NO_RELEVANT_ACTIVE_PRODUCT:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.no_relevant_active_product',
          { name: this.uploadContextName },
        );
      case CENTRAL_MAPPING_UPLOAD_ERROR_CODES.NO_RELEVANT_MARKET:
        return this.intl.t(
          'settings.central_mapping.upload_mapping.error.no_relevant_market',
        );
      case null:
        return this.intl.t('settings.central_mapping.upload_mapping.error');
      default: {
        // The `never` typing catches a new code missing a case at compile
        // time. This runtime fallback covers it too, in case that ever slips
        // through, the same way an unrecognized code from the backend does.
        const unhandledCode: never = info.code;

        this.errorTracking.captureException(
          new Error(
            `Central mapping upload error code has no message: ${String(unhandledCode)}`,
          ),
        );

        return this.intl.t('settings.central_mapping.upload_mapping.error');
      }
    }
  }

  hasActivePropertyFilters(
    filterOptions: CentralMappingFilterQueryParams,
  ): boolean {
    const { properties = '', brands = '', chains = '' } = filterOptions;
    return brands?.length > 0 || chains?.length > 0 || properties?.length > 0;
  }

  async uploadMappingEntries(
    file: File,
    breakdown: Breakdown,
    valueType: PmsValueType,
  ): Promise<void> {
    const uploadURL = new URL(
      '/apigateway/v1/app/mapping/mapping_entries/upload/',
      this.ajax.host,
    );

    const params = new URLSearchParams({
      [this.hotelOrMarketKey]:
        this.hotelAndMarketService.hotelOrMarket.id ?? '',
      breakdown,
      value_type: valueType,
    });

    uploadURL.search = params.toString();

    const formData = new FormData();
    formData.append('file', file);

    return this.ajax.request(uploadURL.toString(), {
      method: 'POST',
      data: formData,
      contentType: false,
      processData: false,
      mimeType: 'multipart/form-data',
    });
  }

  @action exportMappingData(
    breakdown: Breakdown,
    valueType: PmsValueType,
    appliedFilters: AppliedMappingFilterIds,
  ): void {
    const queryParams: FormatUrlParams = {
      [this.hotelOrMarketKey]:
        this.hotelAndMarketService.hotelOrMarket.id ?? '',
      breakdown,
      value_type: valueType,
    };

    const {
      barBasedId,
      brandIds,
      categoryIds,
      chainIds,
      commissionableId,
      propertyIds,
      search,
      sortDirectionId,
      viewId,
      includeArchivedId,
    } = appliedFilters;

    if (sortDirectionId) {
      queryParams['order_by'] = `value_${sortDirectionId}`;
    }
    if (includeArchivedId) {
      queryParams['include_archived'] =
        includeArchivedId === CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW;
    }
    if (viewId) {
      queryParams['view'] = viewId;
    }
    if (search) {
      queryParams['search'] = search;
    }
    if (barBasedId) {
      queryParams['bar_based_view'] = barBasedId;
    }
    // Commissionable only applies to market-segment rate plans, so never leak a
    // stale filter value onto other breakdowns/value types.
    if (
      commissionableId &&
      breakdown === BREAKDOWNS.MARKET_SEGMENT &&
      valueType === PMS_VALUE_TYPES.RATE_CODE
    ) {
      queryParams['commissionable_view'] = commissionableId;
    }
    let url = formatUrl(
      `/apigateway/v1/app/download/central_mapping/mapping/mapping_entries`,
      queryParams,
      this.ajax.host,
    );
    if (chainIds?.length) {
      chainIds.forEach((chainId) => {
        url = url.concat(`&chains[]=${chainId}`);
      });
    }
    if (brandIds?.length) {
      brandIds.forEach((brandId) => {
        url = url.concat(`&brands[]=${brandId}`);
      });
    }
    if (propertyIds?.length) {
      propertyIds.forEach((propertyId) => {
        url = url.concat(`&subscriptions[]=${propertyId}`);
      });
    }
    if (categoryIds?.length) {
      categoryIds.forEach((categoryId) => {
        url = url.concat(`&categories[]=${categoryId}`);
      });
    }

    openUrl(url);
  }

  @action
  async onRouteWillChange(
    routeName: string,
    controller: CentralMappingExtendedController,
    transition: Transition,
  ): Promise<void> {
    // Even after saving, hasChanges may remain true while the updateMappingTask is still running.
    // This happens because the task hasn't completed and the controller state isn't updated yet.
    // Therefore, avoid showing the alert if the task is in progress by checking if the task is running.
    if (
      transition.to?.name !== routeName &&
      controller.hasChanges &&
      !this.updateMappingTask.isRunning
    ) {
      transition.abort();

      const canProceedWithAction = controllerIsForMappingRoute(controller)
        ? await controller.handleUnsavedChanges()
        : await this.showUnsavedChangesAlert(controller.discardChanges);

      if (!canProceedWithAction) {
        return;
      }

      transition.retry();
    }
  }

  @action
  async showMarketSegmentationUnsavedChangesAlert(
    saveChangesAndAutomap: () => void,
    valueType: PmsValueType,
  ): Promise<boolean> {
    const isMarketCode = valueType === PMS_VALUE_TYPES.MARKET_CODE;

    const confirmationText = isMarketCode
      ? this.intl.t(
          'central_mapping.confirmation_modal.market-segmentation.action.primary',
        )
      : this.intl.t('general.save_and_continue');

    const confirmationButtonClassName = isMarketCode
      ? 'prism-button prism-button--intelligence'
      : '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('central_mapping.confirmation_modal.title'),
      text: this.intl.t(
        'central_mapping.confirmation_modal.market-segmentation.description',
      ),
      dangerMode: false,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: confirmationText,
          className: confirmationButtonClassName,
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    saveChangesAndAutomap();

    return true;
  }

  @action
  async showUnsavedChangesAlert(discardChanges: () => void): Promise<boolean> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('central_mapping.confirmation_modal.title'),
      text: this.intl.t('central_mapping.confirmation_modal.description'),
      dangerMode: true,
      unsafe_outsideClickCanCloseAlert: false,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'central_mapping.confirmation_modal.action.primary',
          ),
        },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    discardChanges();

    return true;
  }

  @action
  trackMappingEntryChanged({
    mappingChange,
    rowCount,
    rowType,
  }: {
    mappingChange: string;
    rowCount?: number;
    rowType?: 'parent' | 'child' | 'bulk';
  }): void {
    const props = {
      eventTrigger: 'change-mapping',
      mappingChange,
      rowCount: rowCount ?? 1,
      rowType: rowType ?? 'child',
    };

    this.usageAnalytics.track('Mapping Entry Changed', props);
  }

  @action
  async waitForUpdateMappingTask(): Promise<boolean> {
    if (this.updateMappingTask.isRunning) {
      await this.updateMappingTask.last;
    }
    return this.updateMappingTask.last?.isSuccessful ?? false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'central-mapping': CentralMappingService;
  }
}
